<script setup lang="ts">
import type { IconName } from "~/models/Content/ContentProductPageMediaTypes"

interface MediaTypes {
  icon: IconName
  count: number
  text: string
}

const { subjects } = storeToRefs(useProductContentStore())
const { activeVariant } = storeToRefs(useProductVariantsStore())

const mediaCounts = subjects.value.map(subject =>
  useMediaCounts(subject),
)

// Summarize media counts
const summarizedCounts = computed(() => {
  return mediaCounts.reduce(
    (acc, media) => {
      const data = unref(media.data)
      if (data) {
        acc.videos += data.videos
        acc.podcasts += data.podcasts
        acc.quizes += data.quizes
      }
      return acc
    },
    { articles: 0, videos: 0, podcasts: 0, quizes: 0 },
  )
})

const mediaTypes = computed<MediaTypes[]>(() => [
  {
    icon: "article",
    count: subjects.value.length,
    text: subjects.value.length === 1 ? "kapittel" : "kapitler",
  },
  {
    icon: "medal",
    count: summarizedCounts.value.quizes,
    text: summarizedCounts.value.quizes === 1 ? "quiz" : "quizzer",
  },
  {
    icon: "video",
    count: summarizedCounts.value.videos,
    text: summarizedCounts.value.videos === 1 ? "video" : "videoer",
  },
  {
    icon: "headphones",
    count: summarizedCounts.value.podcasts,
    text:
      summarizedCounts.value.podcasts === 1 ? "podcast" : "podcaster",
  },
])
</script>

<template>
  <div
    class="w-full gap-6 rounded-lg bg-[--theme-lightest] p-6 font-national2condensed text-paywall-mobile-stats-md text-[--theme-dark] md:w-auto md:gap-8 md:p-8 md:text-paywall-desktop-stats-md"
  >
    <!-- Paid -->
    <ul
      v-if="!activeVariant?.isFree"
      class="flex translate-x-1/3 flex-col gap-4 md:translate-x-0 md:flex-row md:gap-6"
    >
      <li
        v-for="type in mediaTypes"
        :key="type.icon"
        :class="[{ '!hidden': !type.count }]"
        class="flex items-center gap-2"
      >
        <UIcon
          :name="type.icon"
          size="sm"
          class="rounded-full bg-[--theme-dark] p-2 text-white"
        />
        <span>{{ type.count }}&nbsp;{{ type.text }}</span>
      </li>
    </ul>
    <!-- Free -->
    <ul v-else class="space-y-2">
      <li class="flex items-center gap-2 md:gap-4">
        <UIcon
          name="check-filled"
          class="text-[--theme-primary]"
          size="auto"
        />
        <span>Eksklusive oppdateringer</span>
      </li>
      <li class="flex items-center gap-2 md:gap-4">
        <UIcon name="check-filled" size="auto" />
        <span>Info når produktet kommer for salg</span>
      </li>
    </ul>
  </div>
</template>
